import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Alert,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormText,
  Form,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../constants/ApiService';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import useVendorBranch from '../../utils/useVendorBranch';
import VendorBranchSelector from '../../components/VendorBranchSelector';

const MULTI_PURPOSE_API_GET =
  'api/Multi_Purpose_Type_Master/GetMulti_Purpose_Type_Master';
const MULTI_PURPOSE_API_ADD =
  'api/Multi_Purpose_Type_Master/AddMulti_Purpose_Type_Master';
const MULTI_PURPOSE_API_UPDATE =
  'api/Multi_Purpose_Type_Master/UpdateMulti_Purpose_Type_Master';

const EmployeeAttendancePage = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authState);
  const { isSuperAdmin, isAdmin, hasMultiBranch } = useVendorBranch(user);

  const [selectionData, setSelectionData] = useState({
    vendor_id: user?.vendor_id || 0,
    branch_id: user?.branch_id || 0,
  });

  const [vbErrorMessage, setVbErrorMessage] = useState({});
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().substring(0, 10),
  );
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [error, setError] = useState('');

  const [reasonList, setReasonList] = useState([]);
  const [reasonModal, setReasonModal] = useState(false);
  const [reasonMpData, setReasonMpData] = useState({
    id: 0,
    multi_purpose_flag: 'AttendanceReason',
    multi_purpose_code: '',
    multi_purpose_type: '',
    multi_purpose_remark: '',
  });
  const [reasonMpErrors, setReasonMpErrors] = useState({});

  const toggleReasonModal = () => setReasonModal(!reasonModal);

  useEffect(() => {
    loadReasonList();
  }, []);

  const handleReasonMpChange = (e) => {
    const { name, value } = e.target;
    setReasonMpData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setReasonMpErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
  };

  const validateReasonMp = () => {
    const errs = {};
    if (!reasonMpData.multi_purpose_type?.trim()) {
      errs.multi_purpose_type = 'Reason text is required.';
    }
    return errs;
  };

  const loadReasonList = async () => {
    try {
      const url = `${MULTI_PURPOSE_API_GET}?filter=attendancereason`;
      const res = await apiService.get(url);
      const raw = res?.data ?? res;
      const allData = raw?.data ?? raw ?? [];
      setReasonList(Array.isArray(allData) ? allData : []);
    } catch (err) {
      toast.error('Error while loading attendance reasons');
    }
  };

  const saveReasonMaster = async () => {
    const errs = validateReasonMp();
    if (Object.keys(errs).length > 0) {
      setReasonMpErrors(errs);
      return;
    }

    try {
      const config = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };

      const data = JSON.stringify(reasonMpData);

      let response;
      if (!reasonMpData.id || Number(reasonMpData.id) === 0) {
        response = await apiService.post(MULTI_PURPOSE_API_ADD, data, config);
      } else {
        response = await apiService.put(
          `${MULTI_PURPOSE_API_UPDATE}?id=${reasonMpData.id}`,
          data,
          config,
        );
      }

      const apiRoot = response?.data ?? response;
      const ok =
        apiRoot?.status === true ||
        apiRoot?.Status === true ||
        apiRoot?.status === 'true';

      if (ok) {
        toast.success(apiRoot.message || apiRoot.Message || 'Reason saved.');
        setReasonModal(false);
        setReasonMpData({
          id: 0,
          multi_purpose_flag: 'AttendanceReason',
          multi_purpose_code: '',
          multi_purpose_type: '',
          multi_purpose_remark: '',
        });
        setReasonMpErrors({});
        await loadReasonList();
      } else {
        toast.error(
          apiRoot?.message || apiRoot?.Message || 'Failed to save reason.',
        );
      }
    } catch (err) {
      toast.error('Error while saving reason.');
    }
  };

  const handleLoadEmployees = () => {
    setError('');

    if (isSuperAdmin) {
      if (!selectionData.vendor_id || Number(selectionData.vendor_id) <= 0) {
        toast.error('Please select a Vendor');
        return;
      }
    }

    if (isAdmin || hasMultiBranch) {
      if (!selectionData.branch_id || Number(selectionData.branch_id) <= 0) {
        toast.error('Please select a Branch');
        return;
      }
    }

    if (!selectedDate) {
      setError('Please select a date.');
      return;
    }

    navigate('/list-emp-attandance', {
      state: {
        date: selectedDate,
        startTime,
        endTime,
        reasonList,
        vendorId: selectionData.vendor_id || null,
        branchId: selectionData.branch_id || null,
      },
    });
  };

  return (
    <div className='container-fluid mt-3'>
      <VendorBranchSelector
        user={user}
        selectionData={selectionData}
        setSelectionData={setSelectionData}
        errorMessage={vbErrorMessage}
        accessButton={true}
      />

      <Row>
        <Col md='12'>
          <Card className='card-box height-100-p pd-20 mb-20'>
            <CardBody>
              <Row>
                <Col md='3'>
                  <FormGroup>
                    <Label>Date</Label>
                    <Input
                      type='date'
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                    />
                  </FormGroup>
                </Col>

                <Col md='3'>
                  <FormGroup>
                    <Label>Start Time</Label>
                    <Input
                      type='datetime-local'
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                    />
                  </FormGroup>
                </Col>

                <Col md='3'>
                  <FormGroup>
                    <Label>End Time</Label>
                    <Input
                      type='datetime-local'
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                    />
                  </FormGroup>
                </Col>

                <Col md='3'>
                  <FormGroup>
                    <Label>Action</Label>
                    <Button
                      color='primary'
                      className='text-white w-100'
                      onClick={handleLoadEmployees}
                    >
                      Load Employees
                    </Button>
                  </FormGroup>
                </Col>
              </Row>

              <Row className='mt-2'>
                <Col md='12'>
                  <Button
                    color='secondary'
                    size='sm'
                    onClick={toggleReasonModal}
                  >
                    Add Attendance Reason
                  </Button>
                </Col>
              </Row>

              {error && (
                <Row className='mt-2'>
                  <Col md='12'>
                    <Alert color='danger'>{error}</Alert>
                  </Col>
                </Row>
              )}

              <Modal centered isOpen={reasonModal} toggle={toggleReasonModal}>
                <ModalHeader toggle={toggleReasonModal}>
                  Add new Attendance Reason
                </ModalHeader>
                <ModalBody>
                  <Form>
                    <FormGroup>
                      <Label htmlFor='multi_purpose_type'>
                        Enter new Attendance Reason :
                      </Label>
                      <Input
                        id='multi_purpose_type'
                        name='multi_purpose_type'
                        value={reasonMpData.multi_purpose_type}
                        onChange={handleReasonMpChange}
                      />
                      {reasonMpErrors.multi_purpose_type && (
                        <FormText color='danger'>
                          {reasonMpErrors.multi_purpose_type}
                        </FormText>
                      )}
                    </FormGroup>
                  </Form>
                </ModalBody>
                <ModalFooter>
                  <Button color='primary' onClick={saveReasonMaster}>
                    Submit
                  </Button>
                  <Button color='secondary' onClick={toggleReasonModal}>
                    Cancel
                  </Button>
                </ModalFooter>
              </Modal>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default EmployeeAttendancePage;
